import { Fragment, useState, useEffect, useCallback, useRef } from 'react'
import { errorSentence } from '../lib/errorSentence'
import { useDropzone } from 'react-dropzone'
import { isValidPhoneNumber } from 'react-phone-number-input'
import PhoneInput from 'react-phone-number-input/input'
import { Upload } from '../api'
import type { UploadBatchSummary, UploadBatchItem, SubmissionSummary, CurrentReading } from '../api'
import CorrectionForm from '../components/CorrectionForm'
import { commonName } from '../speciesCommonNames'

const CONCURRENCY = 4
const POLL_MS = 5000

const REASON: Record<string, string> = {
  no_fish: 'No fish found in this photo',
  no_media: 'No photo received',
  non_image_media: 'Not a photo',
  undecodable: "Couldn't read this photo",
  declined_unknown: 'Not recognized',
  failed: 'Something went wrong analyzing this photo',
  no_size: 'Fish found, but the size was unclear',
  pending: 'Analyzing…',
  stalled: "Didn't finish — try uploading this one again",
}

function fmtTaken(d: Date): string {
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const inchesNum = (mm: number) => Math.round((mm / 25.4) * 2) / 2
const inches = (mm: number | null | undefined) => (mm == null ? null : `${inchesNum(mm)} in`)
const poundsNum = (g: number) => Math.round((g / 453.592) * 2) / 2
const pounds = (g: number | null | undefined) => (g == null ? null : `~${poundsNum(g)} lb`)
const isSettled = (b: { doneCount: number; expectedCount: number }) => b.doneCount >= b.expectedCount

function batchHashId(): string | null {
  const h = window.location.hash
  return h.startsWith('#batch=') ? decodeURIComponent(h.slice('#batch='.length)) : null
}


/** Home-list subtitle: "Report ready" once texted, "3 analyzed · 2 didn't finish"
 *  once every photo settled, "3 of 5 analyzed" while still processing. */
function batchProgress(b: UploadBatchSummary): string {
  if (b.notifiedAt) return 'Report ready'
  if (isSettled(b) && b.expectedCount > 0) {
    const ok = `${b.doneCount - b.failedCount} analyzed`
    return b.failedCount > 0 ? `${ok} · ${b.failedCount} didn't finish` : ok
  }
  return `${b.doneCount} of ${b.expectedCount} analyzed`
}

export default function UploadPage() {
  const [authed, setAuthed] = useState<boolean | null>(null)
  const [name, setName] = useState('')
  const [isTeam, setIsTeam] = useState(false)
  const [canUpload, setCanUpload] = useState(false)
  const [batchId, setBatchId] = useState<string | null>(batchHashId)

  const loadMe = useCallback(() => {
    Upload.uploadMe()
      .then(r => { setName(r.name); setIsTeam(r.isTeam); setCanUpload(r.phone != null); setAuthed(true) })
      .catch(() => setAuthed(false))
  }, [])
  useEffect(() => { loadMe() }, [loadMe])

  useEffect(() => {
    const onHash = () => setBatchId(batchHashId())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  function openBatch(id: string) {
    window.location.hash = `#batch=${encodeURIComponent(id)}`
  }
  function goHome() {
    window.location.hash = ''
  }
  function handleSignedIn() {
    // Re-read /me: a team member who just signed in by phone keeps the team
    // view but can now upload with that phone.
    loadMe()
  }
  async function handleLogout() {
    try { await Upload.uploadLogout() } catch { /* ignore */ }
    setAuthed(false)
  }

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <header className="border-b border-stone-200 bg-white px-4 py-3 flex items-center gap-2.5">
        <img src="/bear_icon_transparent.svg" alt="" className="h-8 w-auto" />
        <h1 className="font-display font-black text-base tracking-widest text-bear uppercase leading-none">
          Kodiak Upload
        </h1>
      </header>
      <main className="flex-1">
        {authed === null && (
          <div className="flex items-center justify-center py-20">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-stone-400 animate-pulse">Loading…</p>
          </div>
        )}
        {authed === false && <SignIn onSignedIn={handleSignedIn} />}
        {authed === true && batchId && <Report batchId={batchId} onBack={goHome} />}
        {authed === true && !batchId && (
          <Home name={name} isTeam={isTeam} canUpload={canUpload} onOpenBatch={openBatch} onLogout={handleLogout} onSignInByPhone={() => setAuthed(false)} />
        )}
      </main>
    </div>
  )
}

function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [step, setStep] = useState<'phone' | 'code'>('phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const phoneIsValid = isValidPhoneNumber(phone, 'US')

  async function sendCode(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await Upload.uploadLoginStart({ uploadLoginStartRequest: { phone } })
      setStep('code')
    } catch (e) {
      setError(await errorSentence(e, 'Could not send a code. Check the number and try again.'))
    } finally {
      setLoading(false)
    }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await Upload.uploadLoginVerify({ uploadLoginVerifyRequest: { phone, code } })
      onSignedIn()
    } catch {
      setError('Invalid or expired code.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
        {step === 'phone' ? (
          <form onSubmit={sendCode} className="space-y-3">
            <p className="font-mono text-xs text-stone-500">
              Enter the phone number you were added with.
            </p>
            <PhoneInput
              country="US"
              value={phone || undefined}
              onChange={v => setPhone(v || '')}
              className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5 font-mono text-sm"
              placeholder="(415) 555-0100"
              autoComplete="tel"
              required
            />
            {error && <p className="font-mono text-[11px] text-coral">{error}</p>}
            <button
              type="submit"
              disabled={loading || !phoneIsValid}
              className="w-full px-4 py-2.5 rounded-xl bg-bear text-white font-mono text-[11px] tracking-[0.15em] uppercase font-semibold hover:bg-bear-light transition-colors disabled:opacity-50"
            >
              {loading ? 'Sending…' : 'Send code'}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="space-y-3">
            <p className="font-mono text-xs text-stone-500">Code sent to {phone}</p>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="123456"
              autoComplete="one-time-code"
              className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5 font-mono text-sm"
              required
            />
            {error && <p className="font-mono text-[11px] text-coral">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2.5 rounded-xl bg-bear text-white font-mono text-[11px] tracking-[0.15em] uppercase font-semibold hover:bg-bear-light transition-colors disabled:opacity-50"
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('phone'); setError(null) }}
              className="w-full font-mono text-[10px] tracking-[0.15em] uppercase text-stone-400 hover:text-stone-700"
            >
              Back
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

interface UploadEntry { name: string; progress: number; error: boolean }

function Home({
  name, isTeam, canUpload, onOpenBatch, onLogout, onSignInByPhone,
}: { name: string; isTeam: boolean; canUpload: boolean; onOpenBatch: (id: string) => void; onLogout: () => void; onSignInByPhone: () => void }) {
  const [batches, setBatches] = useState<UploadBatchSummary[]>([])
  const [uploads, setUploads] = useState<UploadEntry[]>([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [fileCount, setFileCount] = useState(0)
  const [batchesLoaded, setBatchesLoaded] = useState(false)
  const selectedFiles = useRef<File[]>([])

  const loadBatches = useCallback(() => {
    Upload.listBatches({ body: {} })
      .then(r => { setBatches(r.batches); setBatchesLoaded(true) })
      .catch(() => { /* ignore */ })
  }, [])
  useEffect(() => { loadBatches() }, [loadBatches])

  const onDrop = useCallback((accepted: File[]) => {
    selectedFiles.current = accepted
    setFileCount(accepted.length)
    setMessage(null)
  }, [])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // No explicit 'heic' — Safari 17 hands over JPEGs for 'image/*' but
    // converts to HEIC if HEIC is listed explicitly.
    accept: { 'image/*': [] },
  })

  async function startUpload() {
    const files = selectedFiles.current
    if (files.length === 0) return
    setBusy(true)
    setMessage(null)
    setUploads(files.map(f => ({ name: f.name, progress: 0, error: false })))
    try {
      const created = await Upload.createBatch({
        uploadBatchCreateRequest: {
          files: files.map(f => ({ filename: f.name, contentType: f.type || 'application/octet-stream' })),
        },
      })
      const uploaded: { index: number; contentType: string }[] = []
      let next = 0
      const worker = async () => {
        while (next < created.uploads.length) {
          const i = next++
          const slot = created.uploads[i]
          const file = files[i]
          try {
            await fetch(slot.uploadUrl, {
              method: 'PUT',
              body: file,
              headers: { 'Content-Type': file.type || 'application/octet-stream' },
            })
            uploaded.push({ index: i, contentType: file.type || 'application/octet-stream' })
            setUploads(prev => prev.map((u, idx) => (idx === i ? { ...u, progress: 100 } : u)))
          } catch {
            setUploads(prev => prev.map((u, idx) => (idx === i ? { ...u, error: true } : u)))
          }
        }
      }
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, created.uploads.length) }, worker))
      if (uploaded.length === 0) {
        setMessage('None of the photos could be uploaded — check your connection and try again.')
        return
      }
      const finalized = await Upload.finalizeBatch({
        uploadBatchFinalizeRequest: { batchId: created.batchId, uploaded },
      })
      selectedFiles.current = []
      setFileCount(0)
      setUploads([])
      onOpenBatch(finalized.batchId)
    } catch (e) {
      setMessage(await errorSentence(e, 'Upload failed — try again.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <p className="font-mono text-sm text-stone-700">Hi, {name}</p>
        <button onClick={onLogout} className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-400 hover:text-stone-700">
          Sign out
        </button>
      </div>

      {canUpload ? (
        <>
        <div
          {...getRootProps()}
          className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-bear bg-bear/5' : 'border-stone-300 bg-white'
          }`}
        >
          <input {...getInputProps()} />
          <p className="font-mono text-sm text-stone-600">
            {selectedFiles.current.length > 0
              ? `${selectedFiles.current.length} photo${selectedFiles.current.length === 1 ? '' : 's'} selected`
              : 'Tap to choose photos, or drag them in'}
          </p>
        </div>

        {message && <p className="font-mono text-[11px] text-coral">{message}</p>}

        {uploads.length > 0 && (
          <div className="space-y-1.5">
            {uploads.map((u, i) => (
              <div key={i} className="flex items-center gap-2 text-xs font-mono text-stone-500">
                <span className="flex-1 truncate">{u.name}</span>
                <span>{u.error ? '✗' : u.progress === 100 ? '✓' : '…'}</span>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={startUpload}
          disabled={busy || fileCount === 0}
          className="w-full px-4 py-3 rounded-xl bg-bear text-white font-mono text-[11px] tracking-[0.15em] uppercase font-semibold hover:bg-bear-light transition-colors disabled:opacity-50"
        >
          {busy ? 'Uploading…' : fileCount > 0 ? `Upload ${fileCount} photo${fileCount === 1 ? '' : 's'}` : 'Upload photos'}
        </button>
        </>
      ) : (
        <button
          onClick={onSignInByPhone}
          className="w-full px-4 py-3 rounded-xl border border-bear text-bear font-mono text-[11px] tracking-[0.15em] uppercase font-semibold hover:bg-bear/5 transition-colors"
        >
          Sign in with your phone to upload
        </button>
      )}

      {batchesLoaded && batches.length === 0 && (
        <p className="mt-6 text-sm text-stone-500">No batches yet{isTeam ? '.' : ' — your reports will show up here after your first upload.'}</p>
      )}
      {batches.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">{isTeam ? 'Batches' : 'My batches'}</h2>
          {batches.map(b => (
            <button
              key={b.batchId}
              onClick={() => onOpenBatch(b.batchId)}
              className="w-full flex items-center gap-3 bg-white border border-stone-200 rounded-lg p-3 text-left hover:border-bear/60 transition-colors"
            >
              <div className="w-14 h-14 rounded bg-stone-200 flex-shrink-0 overflow-hidden">
                {b.coverUrl && <img src={b.coverUrl} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-xs text-stone-700">
                  {isTeam && <span className="font-semibold">{b.senderName ?? 'Unknown'} · </span>}
                  {new Date(b.createdAt).toLocaleDateString()}
                </p>
                <p className="font-mono text-[11px] text-stone-500">
                  {batchProgress(b)}
                </p>
              </div>
            </button>
          ))}
        </section>
      )}
    </div>
  )
}

// ---- report ----------------------------------------------------------------

type Phase = 'loading' | 'ready' | 'error'

const FISH_STATUSES = new Set(['replied', 'no_size'])
const isFish = (item: UploadBatchItem) =>
  item.detail != null && FISH_STATUSES.has(item.status) && !item.detail.current.notFish
const isNoFish = (item: UploadBatchItem) =>
  item.status === 'no_fish' || (item.detail?.current.notFish ?? false)
// Only photos the analysis actually finished can be reviewed; a failed or
// stalled one has nothing to correct.
const REVIEWABLE_STATUSES = new Set(['replied', 'no_size', 'no_fish'])
const reviewable = (item: UploadBatchItem) => item.detail != null && REVIEWABLE_STATUSES.has(item.status)

function Report({ batchId, onBack }: { batchId: string; onBack: () => void }) {
  const [phase, setPhase] = useState<Phase>('loading')
  const [stale, setStale] = useState(false)
  const [batch, setBatch] = useState<UploadBatchSummary | null>(null)
  const [items, setItems] = useState<UploadBatchItem[]>([])
  const [open, setOpen] = useState<number | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    let ready = false
    let timer: ReturnType<typeof setTimeout> | undefined
    setPhase('loading')
    setStale(false)
    setBatch(null)
    setItems([])
    setOpen(null)
    async function poll() {
      try {
        const r = await Upload.getBatch({ uploadBatchGetRequest: { batchId } })
        if (cancelled) return
        ready = true
        setBatch(r.batch)
        setItems(r.items)
        setPhase('ready')
        setStale(false)
        if (!isSettled(r.batch)) timer = setTimeout(poll, POLL_MS)
      } catch {
        if (cancelled) return
        // Before the first response there is nothing to show but the error;
        // after it, keep the report on screen and keep trying.
        if (ready) {
          setStale(true)
          timer = setTimeout(poll, POLL_MS)
        } else {
          setPhase('error')
        }
      }
    }
    void poll()
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [batchId, attempt])

  // After a save: one cheap batch fetch (the sender cookie can't call the
  // team-only submission endpoints) so the summary, card, and sheet agree.
  const refresh = useCallback(async () => {
    const r = await Upload.getBatch({ uploadBatchGetRequest: { batchId } })
    setBatch(r.batch)
    setItems(r.items)
  }, [batchId])

  const closeSheet = useCallback(() => setOpen(null), [])
  const openable = items.filter(reviewable)
  const openPos = open == null ? -1 : openable.findIndex(i => i.index === open)
  const openItem = openPos >= 0 ? openable[openPos] : null

  // The sheet sits outside the page column so the column's spacing rules
  // don't push the fixed overlay down.
  return (
    <>
    <div className="max-w-3xl mx-auto px-4 py-4 sm:py-6 space-y-4">
      <button onClick={onBack} className="font-mono text-[11px] tracking-[0.15em] uppercase text-stone-400 hover:text-stone-700 py-2">
        ← My batches
      </button>

      {phase === 'loading' && <ReportSkeleton />}

      {phase === 'error' && (
        <div className="bg-white border border-stone-200 rounded-2xl p-6 text-center space-y-3">
          <p className="font-mono text-sm text-stone-700">Couldn't load this report.</p>
          <p className="font-mono text-[11px] text-stone-500">Check your connection and try again.</p>
          <button
            onClick={() => setAttempt(a => a + 1)}
            className="w-full px-4 py-3 rounded-xl bg-bear text-white font-mono text-[11px] tracking-[0.15em] uppercase font-semibold hover:bg-bear-light"
          >
            Retry
          </button>
        </div>
      )}

      {phase === 'ready' && batch && (
        <>
          {stale && (
            <p className="font-mono text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Connection lost — retrying…
            </p>
          )}
          <BatchSummary batch={batch} items={items} />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
            {items.map(item => (
              <ReportCard key={item.index} item={item} eager={item.index < EAGER_CARDS} onOpen={() => setOpen(item.index)} />
            ))}
          </div>
        </>
      )}
    </div>

      {openItem && openItem.detail && (
        <ReviewSheet
          key={openItem.index}
          item={openItem}
          detail={openItem.detail}
          position={openPos + 1}
          total={openable.length}
          onPrev={openPos > 0 ? () => setOpen(openable[openPos - 1].index) : undefined}
          onNext={openPos < openable.length - 1 ? () => setOpen(openable[openPos + 1].index) : undefined}
          onClose={closeSheet}
          onSaved={refresh}
        />
      )}
    </>
  )
}

function ReportSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" aria-busy="true">
      <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-3">
        <div className="h-3 w-40 bg-stone-200 rounded" />
        <div className="h-3 w-64 bg-stone-200 rounded" />
        <div className="h-2 w-full bg-stone-100 rounded" />
      </div>
      <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-stone-400 text-center">Loading your report…</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="aspect-square bg-stone-200" />
            <div className="p-2 space-y-1.5">
              <div className="h-2.5 w-3/4 bg-stone-200 rounded" />
              <div className="h-2.5 w-1/2 bg-stone-100 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

function speciesName(r: CurrentReading): string {
  return r.species.common ?? (r.species.scientific ? commonName(r.species.scientific) ?? r.species.scientific : 'Unidentified')
}

function BatchSummary({ batch, items }: { batch: UploadBatchSummary; items: UploadBatchItem[] }) {
  const fish = items.filter(isFish)
  const noFish = items.filter(isNoFish).length
  const unfinished = batch.failedCount
  const settled = isSettled(batch) && batch.expectedCount > 0
  const pct = batch.expectedCount ? Math.round((batch.doneCount / batch.expectedCount) * 100) : 0

  const species = new Map<string, number>()
  for (const f of fish) {
    const name = speciesName(f.detail!.current)
    species.set(name, (species.get(name) ?? 0) + 1)
  }
  const speciesList = Array.from(species.entries()).sort((a, b) => b[1] - a[1])
  const lengths = fish.map(f => f.detail!.current.length.value).filter((v): v is number => v != null).map(inchesNum)
  const weights = fish.map(f => f.detail!.current.weight.value).filter((v): v is number => v != null).map(poundsNum)
  const reviewed = fish.filter(f => f.detail!.current.state !== 'unchecked').length

  const outcome: string[] = []
  if (fish.length) outcome.push(`${fish.length} fish`)
  if (noFish) outcome.push(`${noFish} no fish`)
  if (unfinished) outcome.push(`${unfinished} didn't finish`)

  return (
    <section className="bg-white border border-stone-200 rounded-2xl p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-xs text-stone-500">
          Uploaded {new Date(batch.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          {' · '}{batch.expectedCount} photo{batch.expectedCount === 1 ? '' : 's'}
        </p>
        {batch.notifiedAt && <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-emerald-700">Report texted</span>}
      </div>

      {!settled ? (
        <div className="space-y-1.5">
          <div className="h-1.5 w-full bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full bg-bear rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="font-mono text-sm text-stone-800">{batch.doneCount} of {batch.expectedCount} analyzed…</p>
        </div>
      ) : (
        <p className="font-mono text-sm text-stone-800">{outcome.join(' · ')}</p>
      )}

      {speciesList.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {speciesList.map(([name, n]) => (
            <span key={name} className="font-mono text-[11px] bg-stone-100 text-stone-700 rounded-full px-2.5 py-1">
              {name} <span className="text-stone-400">{n}</span>
            </span>
          ))}
        </div>
      )}

      {(lengths.length >= 2 || weights.length >= 2) && (
        <p className="font-mono text-[11px] text-stone-600">
          {lengths.length >= 2 && <>Length {Math.min(...lengths)}–{Math.max(...lengths)} in · median {median(lengths)} in</>}
          {lengths.length >= 2 && weights.length >= 2 && ' · '}
          {weights.length >= 2 && <>Weight ~{Math.min(...weights)}–{Math.max(...weights)} lb</>}
        </p>
      )}

      {fish.length > 0 && (
        <p className="font-mono text-[11px] text-stone-500">
          {reviewed} reviewed · {fish.length - reviewed} to check
        </p>
      )}
    </section>
  )
}

/** Thumbnail with a one-time fallback to the full photo for rows analyzed
 *  before thumbnails existed (their thumb URL 404s). The first screen's
 *  images load eagerly so nothing pops in on open; the rest wait until
 *  they're near the viewport. */
function Photo({ d, className, eager = false }: { d: SubmissionSummary; className: string; eager?: boolean }) {
  const [src, setSrc] = useState<string | null>(d.thumbUrl ?? d.photoUrl ?? null)
  if (!src) return null
  return (
    <img
      src={src}
      alt=""
      width={640}
      height={640}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      className={className}
      onError={() => { if (d.photoUrl && src !== d.photoUrl) setSrc(d.photoUrl) }}
    />
  )
}

function readingParts(r: CurrentReading): string[] {
  const parts: string[] = []
  const len = inches(r.length.value)
  const wt = pounds(r.weight.value)
  if (len) parts.push(len)
  if (wt) parts.push(wt)
  return parts
}

const EAGER_CARDS = 6

function ReportCard({ item, eager, onOpen }: { item: UploadBatchItem; eager: boolean; onOpen: () => void }) {
  const d = item.detail
  const fish = isFish(item)
  const reviewed = fish && d!.current.state !== 'unchecked'
  const canOpen = reviewable(item)

  return (
    <button
      type="button"
      onClick={canOpen ? onOpen : undefined}
      disabled={!canOpen}
      className={`block w-full text-left bg-white border rounded-lg overflow-hidden ${
        reviewed ? 'border-emerald-300 border-l-4' : 'border-stone-200'
      } ${canOpen ? 'active:bg-stone-50' : 'cursor-default'}`}
    >
      <div className="aspect-square bg-stone-200">
        {d && <Photo key={d.id} d={d} eager={eager} className="w-full h-full object-cover" />}
      </div>
      <div className="p-2 space-y-0.5">
        {fish ? (
          <>
            <p className="font-mono text-xs text-stone-800 truncate">{speciesName(d!.current)}</p>
            <p className="font-mono text-[11px] text-stone-600 truncate">{readingParts(d!.current).join(' · ') || '—'}</p>
          </>
        ) : (
          <p className="font-mono text-[11px] text-stone-500 leading-snug">{REASON[item.status] ?? item.status}</p>
        )}
      </div>
      {canOpen && (
        <div className={`min-h-[44px] flex items-center justify-between px-2 border-t ${
          reviewed ? 'border-emerald-100 text-emerald-700' : 'border-stone-100 text-bear'
        }`}>
          <span className="font-mono text-[11px] tracking-[0.1em] uppercase font-semibold">
            {reviewed ? '✓ Reviewed' : 'Review'}
          </span>
          <span aria-hidden="true" className="text-base">›</span>
        </div>
      )}
    </button>
  )
}

const SOURCE_TAG: Record<string, string> = { text: 'texted', form: 'entered' }

function ReviewSheet({
  item, detail, position, total, onPrev, onNext, onClose, onSaved,
}: {
  item: UploadBatchItem
  detail: SubmissionSummary
  position: number
  total: number
  onPrev?: () => void
  onNext?: () => void
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [saved, setSaved] = useState(false)
  const r = detail.current

  // The list stays mounted underneath, so closing returns to the same scroll
  // position; locking body scroll keeps a long sheet from scrolling it.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey) }
  }, [onClose])

  const rows: { label: string; est: string | null; cur: string | null; changed: boolean; source: string }[] = [
    {
      label: 'Species',
      est: r.species.estimateCommon ?? r.species.estimateScientific,
      cur: r.species.common ?? r.species.scientific,
      changed: r.changed.includes('species'),
      source: r.species.source,
    },
    { label: 'Length', est: inches(r.length.estimate), cur: inches(r.length.value), changed: r.changed.includes('length'), source: r.length.source },
    { label: 'Girth', est: inches(r.girth.estimate), cur: inches(r.girth.value), changed: r.changed.includes('girth'), source: r.girth.source },
    { label: 'Weight', est: pounds(r.weight.estimate), cur: pounds(r.weight.value), changed: r.changed.includes('weight'), source: r.weight.source },
  ]

  return (
    <div className="fixed inset-0 z-40 flex sm:items-start sm:justify-center sm:pt-12 sm:px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full h-full sm:h-auto sm:max-h-[88vh] sm:max-w-md bg-white sm:rounded-2xl flex flex-col"
      >
        <div className="flex-1 overflow-y-auto">
          <div className="bg-stone-900 flex items-center justify-center" style={{ maxHeight: '45vh' }}>
            <Photo key={detail.id} d={detail} className="w-full max-h-[45vh] object-contain" />
          </div>
          <div className="p-4 space-y-4">
            <section className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <h3 className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">What we found</h3>
                {(item.capturedAt ?? detail.capturedAt) && (
                  <span className="font-mono text-[10px] text-stone-400">Taken {fmtTaken((item.capturedAt ?? detail.capturedAt)!)}</span>
                )}
              </div>
              {r.notFish ? (
                <p className="font-mono text-sm text-stone-700">Marked as not a fish.</p>
              ) : (
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono text-sm">
                  {rows.map(row => (
                    <Fragment key={row.label}>
                      <dt className="text-stone-500">{row.label}</dt>
                      <dd className={row.changed ? 'text-amber-900' : 'text-stone-800'}>
                        {row.changed && row.est && <span className="line-through text-stone-400 mr-1.5">{row.est}</span>}
                        {row.cur ?? <span className="text-stone-400">—</span>}
                        {row.source !== 'estimate' && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-[0.1em] text-stone-400">{SOURCE_TAG[row.source] ?? row.source}</span>
                        )}
                      </dd>
                    </Fragment>
                  ))}
                </dl>
              )}
              {!fishStatus(item.status) && (
                <p className="font-mono text-[11px] text-stone-500">{REASON[item.status] ?? item.status}</p>
              )}
            </section>

            <section className="space-y-1.5">
              <div className="flex items-center justify-between">
                <h3 className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">Correct it</h3>
                {saved && <span className="font-mono text-[11px] text-emerald-700">Saved ✓</span>}
              </div>
              <CorrectionForm
                key={detail.id + detail.correctionCount}
                submissionId={detail.id}
                reading={r}
                onSaved={() => {
                  void onSaved().then(() => { setSaved(true); setTimeout(() => setSaved(false), 2000) })
                }}
              />
            </section>
          </div>
        </div>

        <div className="border-t border-stone-200 bg-white px-3 py-2 flex items-center gap-2" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
          <button type="button" onClick={onPrev} disabled={!onPrev}
            className="min-w-[44px] min-h-[44px] rounded-lg text-xl text-stone-600 disabled:text-stone-300 active:bg-stone-100">‹</button>
          <span className="font-mono text-[11px] text-stone-500 flex-1 text-center">{position} of {total}</span>
          <button type="button" onClick={onNext} disabled={!onNext}
            className="min-w-[44px] min-h-[44px] rounded-lg text-xl text-stone-600 disabled:text-stone-300 active:bg-stone-100">›</button>
          <button type="button" onClick={onClose}
            className="min-h-[44px] px-4 rounded-lg bg-bear text-white font-mono text-[11px] tracking-[0.15em] uppercase font-semibold">Done</button>
        </div>
      </div>
    </div>
  )
}

const fishStatus = (status: string) => FISH_STATUSES.has(status)
